/**
 * Shared player-friendly date/time formatting.
 *
 * Backend timestamps stay raw ISO on the wire; these helpers render them in the
 * player's own locale/timezone (Dashboard "Checked at", Gold Exchange/Spinner
 * quote "Expires", …). Invalid/missing inputs degrade gracefully instead of
 * leaking raw strings or throwing.
 */

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

/**
 * "Today, 14:19" / "Jul 2, 14:19" (browser locale + timezone).
 * Returns em-dash for missing/invalid timestamps.
 */
export function formatLocalDateTime(iso: string | null | undefined, now: Date = new Date()): string {
  const date = parseIso(iso);
  if (!date) return '—';
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) return `Today, ${time}`;
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${day}, ${time}`;
}

/**
 * "9 July, 17:32" in the player's timezone but ALWAYS with an English month
 * name, for surfaces the owner wants locale-independent (Spinner weekly
 * cosmetic availability). Returns em-dash for missing/invalid timestamps.
 */
export function formatEnglishDateTime(iso: string | null | undefined): string {
  const date = parseIso(iso);
  if (!date) return '—';
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}, ${time}`;
}

/** "1m 42s" / "2h 05m" / "3d 4h" chunks for a non-negative ms duration. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * Relative expiry: "Expires in 11m 42s" while active, "Expired" after.
 * Returns em-dash for missing/invalid timestamps.
 */
export function formatExpiresIn(iso: string | null | undefined, nowMs: number = Date.now()): string {
  const date = parseIso(iso);
  if (!date) return '—';
  const remaining = date.getTime() - nowMs;
  if (remaining <= 0) return 'Expired';
  return `Expires in ${formatDuration(remaining)}`;
}

/** Milliseconds until `iso` (0 when past/invalid/missing). */
export function remainingMs(iso: string | null | undefined, nowMs: number = Date.now()): number {
  const date = parseIso(iso);
  if (!date) return 0;
  return Math.max(0, date.getTime() - nowMs);
}

/** Tick all `[data-expires-countdown]` labels (Gold Exchange order expiry). */
export function updateExpiryCountdowns(root: Pick<ParentNode, 'querySelectorAll'>, nowMs = Date.now()): void {
  for (const label of root.querySelectorAll<HTMLElement>('[data-expires-countdown]')) {
    const text = formatExpiresIn(label.dataset.expiresAt ?? null, nowMs);
    label.textContent = text;
    label.classList.toggle('cashout-expires--expired', text === 'Expired');
  }
}

/**
 * Whole-token display for a smallest-unit amount ("100", "166.666667 $OBOL"…).
 * Falls back to the raw unit count when decimals are unknown.
 */
export function formatTokenAmount(raw: number | null | undefined, decimals: number | null | undefined): string {
  if (raw === null || raw === undefined || !Number.isFinite(raw)) return '—';
  if (decimals === null || decimals === undefined || !Number.isInteger(decimals) || decimals < 0) {
    return `${raw} (smallest units)`;
  }
  const value = raw / 10 ** decimals;
  return value.toLocaleString(undefined, { maximumFractionDigits: Math.min(decimals, 6) });
}

/** Like {@link formatTokenAmount} but bigint-safe for balances beyond Number range. */
export function formatRawTokenAmount(raw: bigint | string | null | undefined, decimals: number | null | undefined): string {
  if (raw === null || raw === undefined) return '—';
  let value: bigint;
  try {
    value = typeof raw === 'bigint' ? raw : BigInt(raw);
  } catch {
    return '—';
  }
  if (decimals === null || decimals === undefined || !Number.isInteger(decimals) || decimals < 0) {
    return `${value} (smallest units)`;
  }
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  const frac = value % scale;
  const wholeText = whole.toLocaleString();
  if (frac === 0n) return wholeText;
  const fracText = frac.toString().padStart(decimals, '0').slice(0, Math.min(decimals, 6)).replace(/0+$/, '');
  return fracText.length > 0 ? `${wholeText}.${fracText}` : wholeText;
}

/**
 * Parse a human-typed whole-token amount ("10", "2.5") into raw smallest units
 * using the mint decimals. Exact decimal-string math (no floats). Returns null
 * for empty/invalid/negative input or more fractional digits than the mint has.
 */
export function parseTokenAmountToRaw(input: string, decimals: number): bigint | null {
  if (!Number.isInteger(decimals) || decimals < 0) return null;
  const text = input.trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const [wholePart, fracPart = ''] = text.split('.');
  if (fracPart.length > decimals) return null;
  const raw = BigInt(wholePart!) * 10n ** BigInt(decimals) + BigInt(fracPart.padEnd(decimals, '0') || '0');
  return raw > 0n ? raw : null;
}
